<template>
  <b-col>
    <b-card no-body>
      <b-card-body>
        <h3 class="font-weight-bold font-medium-3 ml-1">
          {{ t('Consignee Informations') }}
        </h3>
        <validation-observer ref="consigneeInformationsForm" tag="form">
          <b-col>
            <validation-provider #default="{ errors }" :name="t('Consignee')" vid="consignee">
              <b-form-group label-for="consignee">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Consignee') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <b-form-input
                  v-model="consigneeInformations.name"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="t('Consignee')"
                  type="text"
                  :disabled="!editable"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              :name="t('Attention Name')"
              :rules="consigneeInformations.attention_name ? '' : 'required'"
              vid="consignee_attention_name"
            >
              <b-form-group label-for="consignee">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Attention Name') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <b-form-input
                  v-model="consigneeInformations.attention_name"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="t('Consignor')"
                  type="text"
                  :disabled="!editable"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              :name="t('Phone Number')"
              :rules="consigneeInformations.phone ? '' : 'required'"
              vid="consignee_phone_number"
            >
              <b-form-group label-for="consignee">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Phone Number') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <b-form-input
                  v-model="consigneeInformations.phone"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="t('Phone Number')"
                  type="text"
                  :disabled="!editable"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              :name="t('Address')"
              rules="required"
              vid="consignee_address"
            >
              <b-form-group label-for="consignee_address">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Address') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <b-form-input
                  v-model="consigneeInformations.address"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="t('Address')"
                  tabindex="4"
                  :disabled="!editable"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              :name="t('Country')"
              rules="required"
              vid="consignee_country"
            >
              <b-form-group label-for="consignee_country" :state="errors.length > 0 ? false : null">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Country') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <v-select
                  id="consignee_country"
                  v-model="consigneeInformations.country"
                  :options="countries"
                  :placeholder="t('Make a Choice')"
                  label="text"
                  @input="getConsigneeStateList"
                  :disabled="!editable"
                >
                  <span slot="no-options">{{ t('No records found') }}</span>
                </v-select>
                <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <b-row>
              <b-col md="6" lg="4">
                <b-form-group label-for="consignee_postal_code">
                  <validation-provider
                    #default="{ errors }"
                    :name="t('Postal Code')"
                    :rules="consigneePostalCodeRequired ? 'required' : null"
                    vid="consignee_postal_code"
                    ref="consigneePostalCodeValidation"
                  >
                    <div class="d-flex form-group-label">
                      <label for="name">{{ t('Postal Code') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <v-select
                      :disabled="!(consigneePostalCodeRequired && editable)"
                      v-model="consigneeInformations.postal_code"
                      :loading="consigneePostalCodeLoading"
                      :options="postalCodeOptions"
                      :placeholder="t('Enter Postal Code')"
                      label="combinedLabel"
                      @input="handleConsigneeFromPostalCode"
                      @search="handleConsigneePostalCodeSearch"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6" lg="4">
                <validation-provider
                  #default="{ errors }"
                  ref="consigneeStateValidation"
                  :name="t('City')"
                  rules="required"
                  vid="consignee_state"
                >
                  <b-form-group
                    label-for="consignee_state"
                    :state="errors.length > 0 ? false : null"
                  >
                    <div class="d-flex form-group-label">
                      <label for="name">{{ t('City') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <v-select
                      :disabled="consigneePostalCodeRequired"
                      id="consignee_state"
                      v-model="consigneeInformations.state"
                      :options="consigneeStates"
                      :placeholder="t('Make a Choice')"
                      label="text"
                      @input="getConsigneeCityList"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                    <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="6" lg="4">
                <validation-provider
                  #default="{ errors }"
                  ref="consigneeCityValidation"
                  :name="t('District')"
                  rules="required"
                  vid="consignee_city"
                >
                  <b-form-group
                    label-for="consignee_city"
                    :state="errors.length > 0 ? false : null"
                  >
                    <div class="d-flex form-group-label">
                      <label for="name">{{ t('District') }}</label>
                      <span class="text-danger">*</span>
                    </div>
                    <v-select
                      :disabled="consigneePostalCodeRequired"
                      id="consignee_city"
                      v-model="consigneeInformations.city"
                      :options="consigneeCities"
                      :placeholder="t('Make a Choice')"
                      label="text"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                    <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </b-col>
        </validation-observer>
      </b-card-body>
    </b-card>
  </b-col>
</template>
<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BFormInput,
  BFormGroup,
  BFormInvalidFeedback
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';
import debounce from 'lodash.debounce';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BFormInput,
    BFormGroup,
    BFormInvalidFeedback,

    vSelect,

    ValidationProvider,
    ValidationObserver
  },
  mixins: [helper],
  props: {
    consigneeInformations: {
      type: Object
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      consigneePostalCodeRequired: true,
      consigneePostalCodeLoading: false,
      consigneeFilteredPostalCodes: [],
      consigneePostalCodes: [],
      minimumPostalCodeLength: 3
    };
  },
  watch: {
    consigneeInformations: {
      async handler() {
        await this.prepareCountryData();
        await this.prepareStateData();
        await this.prepareCityData();
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters({
      countries: 'app/countries',
      consigneeStates: 'app/consigneeStates',
      consigneeCities: 'app/consigneeCities',
      preferences: 'app/preferences'
    }),
    postalCodeOptions() {
      return this.mapToOptions(this.consigneeFilteredPostalCodes);
    }
  },
  methods: {
    async prepareCountryData() {
      await this.$store.dispatch('app/getCountries');

      this.consigneeInformations.country = this.countries.find(a => {
        return a.iso3 === this.consigneeInformations.countryIso3;
      });

      this.consigneePostalCodeRequired = this.consigneeInformations.country.isPostalCodeRequired;
      if (!this.consigneePostalCodeRequired) {
        this.consigneeInformations.postal_code = '';
        this.$refs.consigneePostalCodeValidation.reset();
      }
    },

    async prepareStateData() {
      await this.$store.dispatch(
        'app/getConsigneeStates',
        this.consigneeInformations.country.value
      );

      this.consigneeInformations.state = this.consigneeStates.find(item => {
        return item.text === this.consigneeInformations.state;
      });
    },

    async prepareCityData() {
      this.$store.dispatch('app/getConsigneeCities', {
        countryId: this.consigneeInformations.country.value,
        cityId: this.consigneeInformations.state.value
      });
    },

    async getConsigneeStateList(data) {
      this.consigneeInformations.state = '';
      this.$refs.consigneeStateValidation.reset();

      this.consigneeInformations.postal_code = '';
      this.$refs.consigneePostalCodeValidation.reset();

      this.getConsigneeCityList('');

      this.consigneePostalCodes = [];
      this.consigneeFilteredPostalCodes = [];

      if (data && data.value) {
        this.consigneePostalCodeRequired = data.isPostalCodeRequired;
        await this.$store.dispatch('app/getConsigneeStates', data.value);
      }
    },
    getConsigneeCityList(data) {
      this.consigneeInformations.city = '';
      this.$refs.consigneeCityValidation.reset();

      if (data && data.value) {
        this.$store.dispatch('app/getConsigneeCities', {
          countryId: this.consigneeInformations.country.value,
          cityId: data.value
        });
      }
    },
    handleConsigneeFromPostalCode(data) {
      if (data) {
        this.consigneeInformations.state = data.state;
        this.consigneeInformations.city = data.city;
      }
    },
    handleConsigneePostalCodeSearch: debounce(function handleConsigneePostalCodeSearch(postalCode) {
      if (postalCode.length >= this.minimumPostalCodeLength) {
        this.filterConsigneePostalCodes(postalCode);
      }
    }, 300),
    filterConsigneePostalCodes(postalCode) {
      const data = {
        country_code: this.consigneeInformations.country.iso2,
        zipcode: postalCode
      };

      this.consigneePostalCodeLoading = true;
      useService
        .autoPostalCodes(data)
        .then(response => {
          this.consigneePostalCodes = response.data;
          const filtered = this.consigneePostalCodes.filter(code =>
            code.zipcode.includes(postalCode)
          );

          this.consigneeFilteredPostalCodes = filtered.map(code => ({
            place: code.place,
            zipcode: code.zipcode,
            state: code.state,
            city: code.province
          }));
        })
        .finally(() => {
          this.consigneePostalCodeLoading = false;
        });
    },
    mapToOptions(list) {
      return list.map(code => ({
        value: code.zipcode,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
